import React, { ReactElement, useEffect, useState } from 'react';
import './index.scss';
import classNames from 'classnames';
import { isPCAdapterActive } from 'lib/utils/pcAdapter';
import { useDialogKeyboard, DialogKeyboardConfig } from 'lib/hooks/useDialogKeyboard';

type FooterType = 'none' | 'middle-confirm' | 'long-confirm' | 'both';

interface GameBaseDialogProps {
  customClass?: string;
  showClose?: boolean;
  backgroudImg: string;
  closeCallback?: () => void;
  children: React.ReactNode;
  footerType?: FooterType; // 新增底部按钮类型控制
  showCancel?: boolean; // 保留旧版兼容
  showConfirm?: boolean | ReactElement; // 保留旧版兼容
  cancelButtonText?: string;
  confirmButtonText?: string | ReactElement;
  title?: string;
  cancelCallback?: () => void;
  confirmCallback?: () => void;
  landscapeBg?: string; // 横屏背景图
  keepSizeConsistent?: boolean; // 新增参数
  showHeadBorder?: boolean; // 新增参数
  keyboard?: DialogKeyboardConfig;
}
const GameBaseDialog = ({
  customClass = '',
  showClose = true,
  closeCallback = undefined,
  backgroudImg,
  children,
  footerType = 'both', // 默认显示两个按钮
  showCancel = true,
  showConfirm = true,
  cancelButtonText = '取消',
  confirmButtonText = '确认',
  title = '',
  cancelCallback = undefined,
  confirmCallback = undefined,
  landscapeBg,
  keepSizeConsistent = false, // 默认值
  showHeadBorder = false,
  keyboard,
}: GameBaseDialogProps) => {
  const [bgImage, setBgImage] = useState(backgroudImg);
  const wrapperClassName = `game-base-dialog-wrapper ${keepSizeConsistent ? 'keep-size-consistent' : ''}`;
  const enableEnterShortcut = Boolean(showConfirm && confirmCallback);
  const enableEscapeShortcut = Boolean(showClose && closeCallback);

  useDialogKeyboard({
    onEnter: keyboard?.onEnter ?? (enableEnterShortcut ? confirmCallback : false),
    onEscape: keyboard?.onEscape ?? (enableEscapeShortcut ? closeCallback : false),
    allowEnterInInput: keyboard?.allowEnterInInput,
  });

  useEffect(() => {
    if (isPCAdapterActive()) return;

    const handleOrientationChange = () => {
      if (!landscapeBg) return;
      const isPortrait = window.matchMedia('(orientation: portrait)').matches;
      setBgImage(isPortrait ? backgroudImg : landscapeBg);
    };

    handleOrientationChange();

    window.addEventListener('resize', handleOrientationChange);
    return () => window.removeEventListener('resize', handleOrientationChange);
  }, [backgroudImg, landscapeBg]);

  return (
    <div className={customClass}>
      <div className={wrapperClassName}>
        <img className="background" alt="" src={bgImage} />
        <div className="game-base-dialog">
          <div
            className={classNames({
              'game-base-dialog__header': true,
              'show-border': showHeadBorder,
            })}
          >
            {showClose && (
              <div className="game-base-dialog__header__close-wrapper">
                <div className="game-base-dialog__header__close" onClick={closeCallback}></div>
              </div>
            )}
            {title && <div className="game-base-dialog__header-title">{title}</div>}
          </div>
          {children}
          {showCancel || showConfirm ? (
            <div className="game-base-dialog__footer">
              {showCancel ? (
                <div className="game-base-dialog__footer-cancel button cancel" onClick={cancelCallback}>
                  {cancelButtonText}
                </div>
              ) : null}
              {showConfirm ? (
                <div className={`game-base-dialog__footer-confirm button ${footerType}`} onClick={confirmCallback}>
                  {confirmButtonText}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
export default GameBaseDialog;
